import Bugsnag from '@bugsnag/js'
import { Dispatch } from 'react'

import sendTrackingEvent from 'mParticle/sendTrackingEvent'
import { IErrorAction, IPanelLoadingAction } from 'checkout/containers/CheckoutStateReducer'
import { Order } from 'shared/types/Order'
import { Promotion } from 'shared/types/Promotion'
import { isTimeoutError, timeoutMessage } from 'checkout/utils/timeout'
import { t } from 'localization'
import { useActivatePromoCodeMutation } from 'api/promoCodesApi'
import { useAuth } from 'shared/hooks/useAuth'
import { useUpdateOrderMutation } from 'api/ordersApi'

const TP = 'checkout.hooks.usePromotionRTK'

export type ActivatePromotion = {
  promoCode: string
  setFieldError: (field: string, errorMsg: string) => void
}

type UsePromotionProps = {
  dispatch: Dispatch<IErrorAction | IPanelLoadingAction>
  order?: Order
}

export const usePromotionRTK = ({ dispatch, order }: UsePromotionProps) => {
  const [activatePromoCode] = useActivatePromoCodeMutation()
  const [updateOrder] = useUpdateOrderMutation()

  const { logoutUser } = useAuth()

  const fallbackErrorMessage = t(
    `${TP}.requestFailure`,
    'Something went wrong.  Please try again or reach out to support@flightclub.com',
  )

  const trackError = (methodName: string, error: Error) => {
    const notifyError = new Error(error?.data?.messages?.[0] || error?.message || methodName)
    Bugsnag.notify(notifyError, (event) => {
      event.addMetadata('checkout', {
        addressId: order?.addressId,
        billingInfoId: order?.billingInfoId,
        id: order?.id,
        isInstantShip: order?.isInstantShip,
        localizedCreditCents: order?.localizedCreditCents,
        localizedFinalPriceCents: order?.localizedFinalPriceCents,
        localizedPromoCodeValueCents: order?.localizedPromoCodeValueCents,
        localizedShippingCents: order?.localizedShippingCents,
        orderNumber: order?.number,
        paymentType: order?.paymentType,
        shippingAddressInRegion: order?.shippingAddressInRegion,
        shippingServiceLevel: order?.shippingServiceLevel,
        status: order?.status,
      })
      event.addMetadata('error', {
        dataMessages: error?.data?.messages,
        file: 'usePromotionRTK',
        message: error?.message,
        method: methodName,
        status: error?.status,
      })
      event.addMetadata('product', {
        boxCondition: order?.product?.boxCondition,
        consigned: order?.product?.consigned,
        id: order?.product?.id,
        name: order?.product?.productTemplate?.name,
        saleStatus: order?.product?.saleStatus,
        shoeCondition: order?.product?.shoeCondition,
        size: order?.product?.size,
        slug: order?.product?.slug || order?.product?.productTemplate?.slug,
        variantShoeCondition: order?.product?.variantShoeCondition,
      })
      event.context = 'checkout'
      event.severity = 'error'
    })
  }

  const activatePromotionRTK = async ({ promoCode, setFieldError }: ActivatePromotion) => {
    if (!order?.number) {
      throw new Error(
        t(
          `${TP}.activatePromotionError`,
          'Error trying to activate a promo code with a missing order',
        ),
      )
    }
    try {
      dispatch({ type: 'promotionLoading' })
      // Activate promo code
      const activatePromoCodeBody = { code: promoCode }
      const activatedPromoCodes = await activatePromoCode(activatePromoCodeBody).unwrap()
      const activePromotion = activatedPromoCodes?.promoCodes?.[0]

      // Connect promo code to order
      const orderBody = {
        orderNumber: order.number,
        order: {
          promoCodeLedgerId: activePromotion?.promoCodeLedgerId,
        },
      }
      const updatedOrder = await updateOrder(orderBody).unwrap()

      dispatch({ type: 'promotionRequestSuccess', payload: updatedOrder })
      sendTrackingEvent('CHECKOUT_ADD_PROMO_CODE_TAP', {
        promo_code_amount: activePromotion?.valuePriceCents,
        promo_code_entered: promoCode,
        promo_code_id: activePromotion?.promoCodeLedgerId,
        promo_code_name: activePromotion?.title,
      })
    } catch (error) {
      const readableErrorMessage = error?.data?.messages?.[0] || error?.message
      const errorMessage = error?.data?.localized_messages?.[0] || error?.data?.messages?.[0]

      sendTrackingEvent('CHECKOUT_ADD_PROMO_CODE_TAP', {
        error_message: readableErrorMessage,
      })

      if (/promo/i.test(readableErrorMessage)) {
        // Promo related errors are displayed above the input using setFieldError
        setFieldError('code', errorMessage)
        dispatch({ type: 'promotionLoading', payload: false })
        return
      }
      if (isTimeoutError(error)) {
        // General connection errors display above the panel
        dispatch({ type: 'orderRequestFailure', payload: timeoutMessage() })
        await logoutUser()
        return
      }
      trackError('activatePromotionRTK', error)
      dispatch({
        type: 'promotionRequestFailure',
        payload: errorMessage || error?.message || fallbackErrorMessage,
      })
    }
  }

  const removePromotionRTK = async (promotion: Promotion) => {
    if (!order?.number) {
      throw new Error(
        t(`${TP}.removePromotionError`, 'Error trying to remove a promo code from a missing order'),
      )
    }

    try {
      dispatch({ type: 'promotionLoading' })
      // Update order to remove promo code
      const orderBody = {
        orderNumber: order.number,
        order: { removePromoCode: true },
      }
      const updatedOrder = await updateOrder(orderBody).unwrap()

      dispatch({
        type: 'removePromotionRequestSuccess',
        payload: { ...updatedOrder, promoCodeLedgerId: null },
      })
      sendTrackingEvent('CHECKOUT_REMOVE_PROMO_CODE_TAP', {
        promo_code_amount: promotion?.valuePriceCents,
        promo_code_id: promotion?.promoCodeLedgerId,
        promo_code_name: promotion?.title,
      })
    } catch (error) {
      const errorMessage = error?.data?.localized_messages?.[0] || error?.data?.messages?.[0]
      if (isTimeoutError(error)) {
        dispatch({ type: 'orderRequestFailure', payload: timeoutMessage() })
        await logoutUser()
        return
      }
      trackError('removePromotionRTK', error)
      dispatch({
        type: 'promotionRequestFailure',
        payload: errorMessage || error?.message || fallbackErrorMessage,
      })
    }
  }

  return { activatePromotionRTK, removePromotionRTK }
}
